import { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://martianbluecyberdefense.in'
  : 'http://localhost:5000';

const extractYouTubeId = (url) => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : url.trim();
};

export default function CoursesAdmin() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modules, setModules] = useState([]);
  const [newCourse, setNewCourse] = useState({ name: '', slug: '', description: '' });
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState({});
  const fileRefs = useRef({});

  const ADMIN_PASS = 'MBCDAdmin@2025';

  const fetchCourses = async () => {
    const res = await fetch(`${API_BASE}/api/courses`);
    const data = await res.json();
    setCourses(data.courses || []);
  };

  useEffect(() => { if (authed) fetchCourses(); }, [authed]);

  const selectCourse = (course) => {
    if (selected?._id === course._id) {
      setSelected(null);
      setModules([]);
      return;
    }
    setSelected(null);
    setTimeout(() => {
      setSelected(course);
      setModules(JSON.parse(JSON.stringify(course.modules || [])));
    }, 50);
  };

  const addModule = () => {
    setModules([...modules, { id: modules.length + 1, title: '', pdf: { id: '', title: '' }, video: { id: '', title: '' } }]);
  };

  const updateModule = (i, field, value) => {
    const m = [...modules];
    if (field === 'title') m[i].title = value;
    if (field === 'videoUrl') { m[i].video.id = extractYouTubeId(value); m[i].video.title = m[i].title; }
    if (field === 'videoTitle') m[i].video.title = value;
    if (field === 'pdfTitle') m[i].pdf.title = value;
    setModules(m);
  };

  const uploadPdf = async (i, file) => {
    setUploading(u => ({ ...u, [i]: true }));
    const form = new FormData();
    form.append('pdf', file);
    const res = await fetch(`${API_BASE}/api/courses/upload-pdf`, { method: 'POST', body: form });
    const data = await res.json();
    if (data.success) {
      const m = [...modules];
      m[i].pdf.id = data.filename;
      m[i].pdf.title = data.filename;
      setModules(m);
      setStatus(`✅ PDF uploaded: ${data.filename}`);
    } else {
      setStatus('❌ PDF upload failed');
    }
    setUploading(u => ({ ...u, [i]: false }));
  };

  const removeModule = (i) => setModules(modules.filter((_, idx) => idx !== i));

  const saveCourse = async () => {
    const res = await fetch(`${API_BASE}/api/courses/${selected._id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ modules })
    });
    const data = await res.json();
    if (data.success) { setStatus('✅ Saved!'); fetchCourses(); setSelected(data.course); }
    else setStatus('❌ Save failed');
  };

  const createCourse = async () => {
    if (!newCourse.name || !newCourse.slug) return setStatus('❌ Name and slug required');
    const res = await fetch(`${API_BASE}/api/courses/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newCourse)
    });
    const data = await res.json();
    if (data.success) { setStatus('✅ Course created!'); setNewCourse({ name: '', slug: '', description: '' }); fetchCourses(); }
    else setStatus('❌ ' + data.message);
  };

  const S = {
    page: { minHeight: '100vh', background: '#061c36', color: '#e0f0ff', fontFamily: 'Rajdhani, sans-serif', padding: '100px 24px 40px 24px' },
    card: { background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.25)', borderRadius: 14, padding: 28, marginBottom: 24 },
    input: { width: '100%', padding: '12px 16px', borderRadius: 10, background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)', color: '#e0f0ff', fontSize: 16, marginBottom: 14, boxSizing: 'border-box' },
    btn: { padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700 },
    btnBlue: { background: 'rgba(0,212,255,0.2)', border: '1px solid rgba(0,212,255,0.4)', color: '#00d4ff' },
    btnGreen: { background: 'rgba(0,255,128,0.15)', border: '1px solid rgba(0,255,128,0.3)', color: '#00ff80' },
    btnRed: { background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.3)', color: '#ff5050' },
    label: { fontSize: 14, color: '#7ab8d4', marginBottom: 6, display: 'block', fontWeight: 600, letterSpacing: 0.5 },
    moduleCard: { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 12, padding: 20, marginBottom: 16 },
  };

  if (!authed) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...S.card, width: 340, textAlign: 'center' }}>
        <h2 style={{ color: '#00d4ff', marginBottom: 20, fontSize: 28, fontWeight: 800 }}>🔐 Courses Admin</h2>
        <input style={S.input} type="password" placeholder="Admin Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && (password === ADMIN_PASS ? setAuthed(true) : setStatus('❌ Wrong password'))} />
        <button style={{ ...S.btn, ...S.btnBlue, width: '100%' }} onClick={() => password === ADMIN_PASS ? setAuthed(true) : setStatus('❌ Wrong password')}>Login</button>
        {status && <p style={{ color: '#ff5050', marginTop: 10 }}>{status}</p>}
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ color: '#00d4ff', marginBottom: 8, fontSize: 36, fontWeight: 800, letterSpacing: 1 }}>📚 Course Management</h1>
        <p style={{ color: '#a0c8e0', marginBottom: 36, fontSize: 18, fontWeight: 500 }}>Manage courses, modules, videos and PDFs</p>

        {status && <div style={{ ...S.card, borderColor: status.includes('✅') ? 'rgba(0,255,128,0.3)' : 'rgba(255,80,80,0.3)', color: status.includes('✅') ? '#00ff80' : '#ff5050', marginBottom: 16 }}>{status}</div>}

        {/* Create Course */}
        <div style={S.card}>
          <h3 style={{ color: '#00d4ff', marginBottom: 20, fontSize: 22, fontWeight: 700 }}>➕ Create New Course</h3>
          <label style={S.label}>Course Name</label>
          <input style={S.input} placeholder="e.g. Summer School 2025" value={newCourse.name} onChange={e => setNewCourse({ ...newCourse, name: e.target.value })} />
          <label style={S.label}>Slug (URL key)</label>
          <input style={S.input} placeholder="e.g. summer-school-2025" value={newCourse.slug} onChange={e => setNewCourse({ ...newCourse, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} />
          <label style={S.label}>Description</label>
          <input style={S.input} placeholder="Short description" value={newCourse.description} onChange={e => setNewCourse({ ...newCourse, description: e.target.value })} />
          <button style={{ ...S.btn, ...S.btnGreen }} onClick={createCourse}>Create Course</button>
        </div>

        {/* Course List */}
        <div style={S.card}>
          <h3 style={{ color: '#00d4ff', marginBottom: 20, fontSize: 22, fontWeight: 700 }}>📋 Existing Courses</h3>
          {courses.length === 0 && <p style={{ color: '#7ab8d4' }}>No courses yet</p>}
          {courses.map(c => (
            <div key={c._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: selected?._id === c._id ? 'rgba(0,212,255,0.12)' : 'rgba(0,0,0,0.2)', borderRadius: 8, marginBottom: 8, border: selected?._id === c._id ? '1px solid rgba(0,212,255,0.4)' : '1px solid transparent' }}>
              <div>
                <span style={{ fontWeight: 700, color: '#e0f0ff', fontSize: 18 }}>{c.name}</span>
                <span style={{ marginLeft: 12, fontSize: 14, color: '#7ab8d4' }}>{c.slug}</span>
                <span style={{ marginLeft: 12, fontSize: 14, color: '#00d4ff', fontWeight: 600 }}>{c.modules?.length || 0} modules</span>
              </div>
              <button style={{ ...S.btn, ...S.btnBlue, padding: '5px 14px', fontSize: 13 }} onClick={() => selectCourse(c)}>✏️ Edit</button>
            </div>
          ))}
        </div>

        {/* Edit Modules */}
        {selected && (
          <div style={S.card}>
            <h3 style={{ color: '#00d4ff', marginBottom: 8, fontSize: 24, fontWeight: 700 }}>✏️ Editing: {selected.name}</h3>
            <p style={{ color: '#a0c8e0', marginBottom: 24, fontSize: 16 }}>Add/edit modules below</p>

            {modules.map((mod, i) => (
              <div key={i} style={S.moduleCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ color: '#00d4ff', fontWeight: 700, fontSize: 18 }}>Module {i + 1}</span>
                  <button style={{ ...S.btn, ...S.btnRed, padding: '3px 10px', fontSize: 12 }} onClick={() => removeModule(i)}>✕ Remove</button>
                </div>

                <label style={S.label}>Module Title</label>
                <input style={S.input} placeholder="e.g. Introduction to CyberSecurity" value={mod.title} onChange={e => updateModule(i, 'title', e.target.value)} />

                <label style={S.label}>YouTube Link (paste full URL)</label>
                <input style={S.input} placeholder="https://youtu.be/xxxxx or https://youtube.com/watch?v=xxxxx" defaultValue={mod.video.id ? `https://youtu.be/${mod.video.id}` : ''} onBlur={e => updateModule(i, 'videoUrl', e.target.value)} />
                {mod.video.id && <p style={{ fontSize: 11, color: '#00ff80', marginTop: -8, marginBottom: 8 }}>✅ Video ID: {mod.video.id}</p>}

                <label style={S.label}>PDF Upload</label>
                <input ref={el => fileRefs.current[i] = el} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadPdf(i, e.target.files[0])} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <button style={{ ...S.btn, ...S.btnBlue, padding: '6px 14px', fontSize: 13 }} onClick={() => fileRefs.current[i]?.click()}>
                    {uploading[i] ? '⏳ Uploading...' : '📄 Upload PDF'}
                  </button>
                  {mod.pdf.id && <span style={{ fontSize: 12, color: '#00ff80' }}>✅ {mod.pdf.id}</span>}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <button style={{ ...S.btn, ...S.btnBlue }} onClick={addModule}>➕ Add Module</button>
              <button style={{ ...S.btn, ...S.btnGreen }} onClick={saveCourse}>💾 Save Course</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
